import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Empty,
  InputNumber,
  List,
  Modal,
  Select,
  Spin,
  Switch,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import { PieChartOutlined, SaveOutlined, ThunderboltOutlined } from '@ant-design/icons';
import LandingLayout from '../../LandingPagesRedesign/LandingLayoutRedesign';
import { ThemeContext } from '../../../context/ThemeContext';
import {
  buildPortfolio,
  fetchLlmStatus,
  getPortfolioBuilderUrl,
  switchLlmProvider,
} from '../../../services/portfolioConstructionService';
import {
  isSavableIsin,
  saveEnginePortfolioHoldings,
  summarizeSaveResult,
} from '../../../services/saveEnginePortfolio';
import { getApiErrorMessage } from '../../../utils/apiErrorUtils';
import FixedIncomePortfolioCharts from './FixedIncomePortfolioCharts';
import LlmProviderPanel from './LlmProviderPanel';
import MyPortfolioHoldingsTable from './MyPortfolioHoldingsTable';
import { usePortfolioUser } from './usePortfolioUser';
import {
  BADGE_CLASS,
  CAT_COLORS,
  DEFAULT_FORM,
  HORIZON_OPTIONS,
  INVESTOR_OPTIONS,
  RBI_VIEW_OPTIONS,
  RISK_PROFILE_OPTIONS,
  TAX_OPTIONS,
  fmtInr,
  fmtPct,
  ratingClass,
} from './fixedIncomePortfolioConstants';
import './FixedIncomePortfolioBuilderPage.css';

const { Text, Paragraph } = Typography;

const PROFILE_PILL = {
  conservative: 'fipb-pill-c',
  moderate: 'fipb-pill-m',
  balanced: 'fipb-pill-b',
  growth: 'fipb-pill-g',
  aggressive: 'fipb-pill-a',
};

function resolveOllamaModelFromStatus(status) {
  const pulled = status?.pulled_models || [];
  const preferred = status?.active_model || status?.env_ollama_model;
  if (!pulled.length) return preferred || 'mistral:7b';
  const exact = pulled.find(
    (m) => m === preferred || m.startsWith(`${preferred}:`) || preferred?.startsWith(`${m}:`)
  );
  if (exact) return exact;
  const recommended = (status?.recommendations || []).find((r) => r.available);
  if (recommended) {
    const match = pulled.find((m) => m.includes(recommended.model));
    if (match) return match;
  }
  return pulled[0];
}

function buildLlmSwitchPayload(provider, models) {
  const payload = { provider };
  if (provider === 'ollama') payload.model = models.ollamaModel;
  else if (provider === 'anthropic') payload.model = models.anthropicModel;
  else if (provider === 'openai') {
    payload.model = models.openaiModel;
    if (models.openaiBaseUrl) payload.openai_base_url = models.openaiBaseUrl;
  }
  return payload;
}

function activeModelLabel(provider, models) {
  if (provider === 'anthropic') return models.anthropicModel;
  if (provider === 'openai') return models.openaiModel;
  return models.ollamaModel;
}

function AiAnalysisPanel({ analysis }) {
  if (!analysis) {
    return (
      <Empty description='Enable "Include AI analysis" and rebuild to get the LLM narrative.' />
    );
  }

  const badges = {
    '[Ollama]': { label: 'Local · Ollama', className: 'fipb-ai-badge-ollama' },
    '[Anthropic]': { label: 'Anthropic Claude', className: 'fipb-ai-badge-anthropic' },
    '[OpenAI-compat]': { label: 'OpenAI-compat', className: 'fipb-ai-badge-openai' },
  };

  let badge = null;
  let text = analysis;
  const prefix = Object.keys(badges).find((k) => analysis.startsWith(k));
  if (prefix) {
    badge = badges[prefix];
    text = analysis.slice(prefix.length).trim();
  }

  return (
    <div className="fipb-ai-card">
      <div className="fipb-ai-header">
        <div className="fipb-ai-avatar">🤖</div>
        <div>
          <div className="fipb-ai-title">
            AI debt strategist — India markets
            {badge && <Tag className={badge.className}>{badge.label}</Tag>}
          </div>
          <Text type="secondary" className="fipb-ai-sub">
            Narrative only — allocation is rules-based, not LLM-driven
          </Text>
        </div>
      </div>
      <div className="fipb-ai-body">
        {text.split(/\n\n+/).filter(Boolean).map((para, i) => (
          <Paragraph key={i}>{para}</Paragraph>
        ))}
      </div>
    </div>
  );
}

function FixedIncomePortfolioBuilderPage() {
  const { isDark } = useContext(ThemeContext);
  const navigate = useNavigate();
  const { requireAuth } = usePortfolioUser();
  const [form, setForm] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(false);
  const [portfolio, setPortfolio] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [llmStatus, setLlmStatus] = useState(null);
  const [llmProvider, setLlmProvider] = useState('ollama');
  const [llmModel, setLlmModel] = useState('mistral:7b');
  const [anthropicModel, setAnthropicModel] = useState('claude-sonnet-4-6');
  const [openaiModel, setOpenaiModel] = useState('gpt-4o');
  const [openaiBaseUrl, setOpenaiBaseUrl] = useState('https://api.openai.com/v1');
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState(null);
  const [lastSaveResult, setLastSaveResult] = useState(null);
  const [holdingsRefreshSignal, setHoldingsRefreshSignal] = useState(0);

  const updateForm = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const loadLlmStatus = useCallback(async () => {
    try {
      const status = await fetchLlmStatus();
      setLlmStatus(status);
      if (status.active_provider) setLlmProvider(status.active_provider);
      const p = status.active_provider || 'ollama';
      if (p === 'anthropic' && status.active_model) {
        setAnthropicModel(status.active_model);
      } else if (p === 'openai' && status.active_model) {
        setOpenaiModel(status.active_model);
      } else {
        setLlmModel(resolveOllamaModelFromStatus(status));
      }
    } catch {
      setLlmStatus(null);
    }
  }, []);

  useEffect(() => {
    loadLlmStatus();
  }, [loadLlmStatus]);

  const handleBuild = async () => {
    setLoading(true);
    setError(null);
    try {
      if (form.include_ai) {
        await switchLlmProvider(
          buildLlmSwitchPayload(llmProvider, {
            ollamaModel: llmModel,
            anthropicModel,
            openaiModel,
            openaiBaseUrl,
          })
        );
        await loadLlmStatus();
      }
      const payload = {
        risk_profile: form.risk_profile,
        investment_amount_inr: form.investment_amount_inr,
        horizon: form.horizon,
        rbi_rate_view: form.rbi_rate_view,
        tax_bracket: form.tax_bracket,
        investor_type: form.investor_type,
        esg_preference: form.esg_preference,
      };
      const result = await buildPortfolio(payload, form.include_ai);
      setPortfolio(result);
      setActiveTab('overview');
      message.success('Portfolio built successfully');
    } catch (e) {
      const detail =
        e?.response?.data?.detail ||
        e?.message ||
        'Could not reach portfolio builder service. Is API Gateway (8080) and AI Portfolio (8090) running?';
      setError(typeof detail === 'string' ? detail : JSON.stringify(detail));
      message.error('Portfolio build failed');
    } finally {
      setLoading(false);
    }
  };

  const savePreview = useMemo(() => {
    if (!portfolio?.holdings?.length) {
      return { savable: [], skipped: [] };
    }
    const savable = [];
    const skipped = [];
    portfolio.holdings.forEach((h) => {
      const isin = String(h.instrument?.isin_prefix || '').trim().toUpperCase();
      if (isSavableIsin(isin)) {
        savable.push(h);
      } else {
        skipped.push(h);
      }
    });
    return { savable, skipped };
  }, [portfolio]);

  const handleOpenSaveModal = () => {
    const auth = requireAuth('Please log in to save holdings to your portfolio');
    if (!auth.ok) return;
    setSaveModalOpen(true);
  };

  const handleConfirmSave = async () => {
    if (!portfolio) return;
    const auth = requireAuth('Please log in to save holdings to your portfolio');
    if (!auth.ok || !auth.userId) return;
    setSaving(true);
    setSaveProgress(null);
    setLastSaveResult(null);
    try {
      const result = await saveEnginePortfolioHoldings(portfolio, auth.userId, {
        onProgress: (p) => setSaveProgress(p),
      });
      setLastSaveResult(result);
      setHoldingsRefreshSignal((n) => n + 1);

      if (result.saved.length > 0) {
        message.success(`Saved ${result.saved.length} holding(s) to My Portfolio`);
      }
      if (result.failed.length > 0) {
        const firstError = result.failed.find((f) => f.error)?.error;
        message.error(
          firstError
            ? `${result.failed.length} holding(s) could not be saved: ${firstError}`
            : `${result.failed.length} holding(s) could not be saved`
        );
      }
      if (result.saved.length === 0 && result.failed.length === 0) {
        message.warning('No bond ISINs to save — only synthetic slots in this allocation');
      }
    } catch (e) {
      message.error(getApiErrorMessage(e, 'Failed to save portfolio'));
    } finally {
      setSaving(false);
      setSaveProgress(null);
    }
  };

  const metrics = portfolio?.metrics;
  const sortedCategories = metrics
    ? Object.entries(metrics.category_distribution)
        .filter(([, v]) => v > 0)
        .sort(([, a], [, b]) => b - a)
    : [];

  const overviewTab = portfolio && metrics && (
    <>
      <div className="fipb-metrics-grid">
        <Card size="small" className="fipb-metric-card">
          <div className="fipb-metric-label">Portfolio value</div>
          <div className="fipb-metric-value">{fmtInr(metrics.total_value_inr)}</div>
        </Card>
        <Card size="small" className="fipb-metric-card">
          <div className="fipb-metric-label">Weighted yield</div>
          <div className="fipb-metric-value fipb-metric-green">{fmtPct(metrics.weighted_yield_pct)}</div>
        </Card>
        <Card size="small" className="fipb-metric-card">
          <div className="fipb-metric-label">Annual income</div>
          <div className="fipb-metric-value">{fmtInr(metrics.annual_income_inr)}</div>
          <div className="fipb-metric-sub">estimated</div>
        </Card>
        <Card size="small" className="fipb-metric-card">
          <div className="fipb-metric-label">Avg duration</div>
          <div className="fipb-metric-value">{metrics.weighted_duration_years.toFixed(1)}y</div>
        </Card>
        <Card size="small" className="fipb-metric-card">
          <div className="fipb-metric-label">Max drawdown</div>
          <div className="fipb-metric-value fipb-metric-red">~{metrics.estimated_max_drawdown_pct.toFixed(1)}%</div>
        </Card>
        <Card size="small" className="fipb-metric-card">
          <div className="fipb-metric-label">Instruments</div>
          <div className="fipb-metric-value">{metrics.number_of_instruments}</div>
        </Card>
      </div>

      <Card className="fipb-alloc-card" size="small">
        <div className="fipb-section-label">Asset class allocation</div>
        <div className="fipb-alloc-bar">
          {sortedCategories.map(([cat, pct]) => (
            <div
              key={cat}
              className="fipb-alloc-segment"
              style={{ flex: pct, background: CAT_COLORS[cat] || '#888' }}
              title={`${cat}: ${pct.toFixed(1)}%`}
            />
          ))}
        </div>
        <div className="fipb-alloc-legend">
          {sortedCategories.map(([cat, pct]) => (
            <span key={cat} className="fipb-leg-item">
              <span className="fipb-leg-dot" style={{ background: CAT_COLORS[cat] || '#888' }} />
              {cat} <strong>{pct.toFixed(1)}%</strong>
            </span>
          ))}
        </div>
      </Card>

      {portfolio.warnings?.length > 0 && (
        <div className="fipb-warnings">
          {portfolio.warnings.map((w, i) => (
            <Alert key={i} type="warning" showIcon message={w} />
          ))}
        </div>
      )}

      <div className="fipb-disclaimer">
        <strong>Disclaimers:</strong> {portfolio.disclaimers?.join(' · ')}
      </div>
    </>
  );

  const holdingsTab = portfolio && (
    <div className="fipb-table-wrap">
      <table className="fipb-holdings-table">
        <thead>
          <tr>
            <th>Instrument</th>
            <th>Weight</th>
            <th>Amount (₹)</th>
            <th>Yield</th>
            <th>Duration</th>
            <th>Rating</th>
            <th>NRI</th>
          </tr>
        </thead>
        <tbody>
          {portfolio.holdings.map((h) => {
            const inst = h.instrument;
            const maxW = Math.max(...portfolio.holdings.map((x) => x.weight_pct));
            const barW = Math.round((h.weight_pct / maxW) * 100);
            return (
              <tr key={inst.isin_prefix + h.amount_inr}>
                <td>
                  <div className="fipb-inst-name">{inst.name}</div>
                  <div className="fipb-inst-meta">
                    <span className={`fipb-badge ${BADGE_CLASS[inst.category] || 'fipb-badge-gsec'}`}>
                      {inst.category}
                    </span>
                    <span className="fipb-isin">{inst.isin_prefix}</span>
                  </div>
                </td>
                <td>
                  <div className="fipb-weight-cell">
                    <div className="fipb-wbar-bg">
                      <div className="fipb-wbar-fill" style={{ width: `${barW}%` }} />
                    </div>
                    <span>{h.weight_pct.toFixed(1)}%</span>
                  </div>
                </td>
                <td>{fmtInr(h.amount_inr)}</td>
                <td className="fipb-yield">{inst.indicative_yield.toFixed(2)}%</td>
                <td>
                  {inst.modified_duration < 1
                    ? inst.modified_duration.toFixed(2)
                    : inst.modified_duration.toFixed(1)}
                  y
                </td>
                <td>
                  <span className={ratingClass(inst.credit_rating)}>{inst.credit_rating}</span>
                </td>
                <td>
                  {inst.nri_eligible ? (
                    <Tag className="fipb-tag-nri">NRI</Tag>
                  ) : (
                    <Tag className="fipb-tag-res">Resident</Tag>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const taxTab = portfolio && (
    <>
      <div className="fipb-table-wrap">
        <table className="fipb-holdings-table fipb-tax-table">
          <thead>
            <tr>
              <th>Instrument</th>
              <th>Category</th>
              <th>Est. income (₹)</th>
              <th>Tax treatment</th>
            </tr>
          </thead>
          <tbody>
            {portfolio.tax_summary.map((ts, i) => (
              <tr key={i}>
                <td className="fipb-inst-name">
                  {ts.instrument_name.length > 38
                    ? `${ts.instrument_name.slice(0, 36)}…`
                    : ts.instrument_name}
                </td>
                <td>
                  <span className={`fipb-badge ${BADGE_CLASS[ts.category] || 'fipb-badge-gsec'}`}>
                    {ts.category}
                  </span>
                </td>
                <td>{fmtInr(ts.estimated_annual_income_inr)}</td>
                <td className="fipb-tax-treatment">{ts.tax_treatment}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="fipb-tax-note">
        <strong>India tax notes:</strong> Debt MF gains taxed at slab rate (no LTCG benefit post Apr 2023).
        G-Sec / SDL coupons taxable at slab; LTCG 10% on capital gains (listed, &gt;1yr). SGB coupon taxable;
        capital gain exempt on RBI maturity. InvIT/REIT distributions partly exempt. AT1/Tier-2 coupon at slab.
        MLD: LTCG 10% or STCG 30%. <em>Consult a qualified CA for your specific tax position.</em>
      </div>
    </>
  );

  const tabItems = portfolio
    ? [
        { key: 'overview', label: 'Overview', children: overviewTab },
        { key: 'holdings', label: 'Holdings', children: holdingsTab },
        { key: 'charts', label: 'Analytics', children: <FixedIncomePortfolioCharts data={portfolio} /> },
        { key: 'tax', label: 'Tax impact', children: taxTab },
        { key: 'ai', label: 'AI analysis', children: <AiAnalysisPanel analysis={portfolio.ai_analysis} /> },
      ]
    : [];

  return (
    <LandingLayout>
      <div className={`fipb-page${isDark ? ' dark' : ''}`}>
        <div className="fipb-shell">
          <aside className="fipb-sidebar">
            <div className="fipb-sidebar-header">
              <PieChartOutlined className="fipb-logo-icon" />
              <div>
                <div className="fipb-logo-title">Fixed Income Portfolio Builder</div>
                <div className="fipb-logo-sub">Rules-based · India debt universe</div>
              </div>
            </div>

            <LlmProviderPanel
              provider={llmProvider}
              onProviderChange={setLlmProvider}
              model={llmModel}
              onModelChange={setLlmModel}
              anthropicModel={anthropicModel}
              onAnthropicModelChange={setAnthropicModel}
              openaiModel={openaiModel}
              onOpenaiModelChange={setOpenaiModel}
              openaiBaseUrl={openaiBaseUrl}
              onOpenaiBaseUrlChange={setOpenaiBaseUrl}
              llmStatus={llmStatus}
            />

            <div className="fipb-sidebar-section fipb-sidebar-section--inputs">
              <div className="fipb-section-label">📊 Portfolio inputs</div>
              <label className="fipb-field">
                <span>Risk profile</span>
                <Select
                  value={form.risk_profile}
                  onChange={(v) => updateForm('risk_profile', v)}
                  options={RISK_PROFILE_OPTIONS}
                  style={{ width: '100%' }}
                />
              </label>
              <label className="fipb-field">
                <span>Investment (₹)</span>
                <InputNumber
                  value={form.investment_amount_inr}
                  onChange={(v) => updateForm('investment_amount_inr', v || 0)}
                  min={10000}
                  step={10000}
                  style={{ width: '100%' }}
                  formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(v) => Number(String(v).replace(/,/g, ''))}
                />
              </label>
              <label className="fipb-field">
                <span>Horizon</span>
                <Select value={form.horizon} onChange={(v) => updateForm('horizon', v)} options={HORIZON_OPTIONS} style={{ width: '100%' }} />
              </label>
              <label className="fipb-field">
                <span>RBI rate view</span>
                <Select value={form.rbi_rate_view} onChange={(v) => updateForm('rbi_rate_view', v)} options={RBI_VIEW_OPTIONS} style={{ width: '100%' }} />
              </label>
              <label className="fipb-field">
                <span>Tax bracket</span>
                <Select value={form.tax_bracket} onChange={(v) => updateForm('tax_bracket', v)} options={TAX_OPTIONS} style={{ width: '100%' }} />
              </label>
              <label className="fipb-field">
                <span>Investor type</span>
                <Select value={form.investor_type} onChange={(v) => updateForm('investor_type', v)} options={INVESTOR_OPTIONS} style={{ width: '100%' }} />
              </label>
              <div className="fipb-toggle-row">
                <span>ESG / green preference</span>
                <Switch checked={form.esg_preference} onChange={(v) => updateForm('esg_preference', v)} />
              </div>
              <div className="fipb-toggle-row">
                <span>Include AI analysis</span>
                <Switch checked={form.include_ai} onChange={(v) => updateForm('include_ai', v)} />
              </div>
              <Button
                type="primary"
                block
                size="large"
                icon={<ThunderboltOutlined />}
                loading={loading}
                onClick={handleBuild}
                className="fipb-build-btn"
              >
                Build portfolio
              </Button>
              {error && <Alert type="error" message={error} showIcon className="fipb-error" />}
            </div>

           
          </aside>

          <main className="fipb-main">
            <header className="fipb-main-header">
              {portfolio ? (
                <>
                  <div>
                    <h1 className="fipb-title">
                      {portfolio.profile_label}
                      <span className="fipb-title-hindi">{portfolio.profile_label_hindi}</span>
                    </h1>
                  </div>
                  <Tag className={`fipb-profile-pill ${PROFILE_PILL[portfolio.request?.risk_profile] || 'fipb-pill-m'}`}>
                    {portfolio.profile_label}
                  </Tag>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleOpenSaveModal}
                    className="fipb-save-btn"
                  >
                    Save to My Portfolio
                  </Button>
                </>
              ) : (
                <h1 className="fipb-title">Portfolio dashboard</h1>
              )}
            </header>

            <div className="fipb-main-content">
              {loading && (
                <div className="fipb-state-box">
                  <Spin size="large" />
                  <p>
                    {form.include_ai
                      ? `Building with ${llmProvider} / ${activeModelLabel(llmProvider, {
                          ollamaModel: llmModel,
                          anthropicModel,
                          openaiModel,
                        })}…`
                      : 'Building portfolio…'}
                  </p>
                </div>
              )}

              {!loading && !portfolio && (
                <div className="fipb-state-box">
                  <PieChartOutlined style={{ fontSize: 48, opacity: 0.25 }} />
                  <p>
                    Select a risk profile and click <strong>Build portfolio</strong> to generate an
                    allocation across the India fixed income universe — G-Sec, SDL, PSU, NCD, FD, and more.
                    {form.include_ai && ' AI narrative uses your selected LLM above.'}
                  </p>
                  <Text type="secondary">
                    Ensure the portfolio builder service is running:{' '}
                    <code>PYTHONPATH=. python main.py serve</code>
                  </Text>
                </div>
              )}

              {!loading && portfolio && (
                <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} className="fipb-tabs" />
              )}
            </div>

            {portfolio && (
              <section className="fipb-saved-section">
                <div className="fipb-saved-header">
                  <div>
                    <h2 className="fipb-saved-title">My Portfolio — saved holdings</h2>
                    <Text type="secondary">
                      Bond ISINs saved from the engine appear here and on Construct Portfolio.
                    </Text>
                  </div>
                  <Button type="link" onClick={() => navigate('/my-portfolio')}>
                    View full portfolio →
                  </Button>
                </div>
                <MyPortfolioHoldingsTable
                  refreshSignal={holdingsRefreshSignal}
                  includeTypeColumn
                  title="Saved Portfolio Holdings"
                  emptyText="No saved holdings yet. Build a portfolio and click Save to My Portfolio."
                />
              </section>
            )}
          </main>
        </div>

        <Modal
          title="Save to My Portfolio"
          open={saveModalOpen}
          onCancel={() => !saving && setSaveModalOpen(false)}
          footer={[
            <Button key="cancel" onClick={() => setSaveModalOpen(false)} disabled={saving}>
              Cancel
            </Button>,
            <Button
              key="save"
              type="primary"
              loading={saving}
              disabled={savePreview.savable.length === 0}
              onClick={handleConfirmSave}
            >
              Save {savePreview.savable.length} bond{savePreview.savable.length === 1 ? '' : 's'}
            </Button>,
          ]}
          width={560}
        >
          <Paragraph>
            Upserts each real ISIN into your SimplyBond portfolio (same API as strategy pages).
            Holdings are tagged by risk profile — e.g. Moderate → Regular Income.
          </Paragraph>
          {saveProgress && (
            <Alert
              type="info"
              showIcon
              message={`Saving ${saveProgress.index} of ${saveProgress.total}: ${saveProgress.name}`}
              style={{ marginBottom: 12 }}
            />
          )}
          {lastSaveResult && !saving && (
            <Alert
              type={lastSaveResult.failed.length ? 'warning' : 'success'}
              showIcon
              message={summarizeSaveResult(lastSaveResult)}
              style={{ marginBottom: 12 }}
            />
          )}
          <List
            size="small"
            header={<Text strong>Will save ({savePreview.savable.length})</Text>}
            dataSource={savePreview.savable}
            locale={{ emptyText: 'No bond ISINs in this allocation' }}
            renderItem={(h) => (
              <List.Item>
                <List.Item.Meta
                  title={h.instrument.name}
                  description={`${h.instrument.isin_prefix} · ${fmtInr(h.amount_inr)} · ${h.weight_pct.toFixed(1)}%`}
                />
              </List.Item>
            )}
          />
          {savePreview.skipped.length > 0 && (
            <List
              size="small"
              style={{ marginTop: 12 }}
              header={<Text type="secondary">Skipped — synthetic / non-ISIN ({savePreview.skipped.length})</Text>}
              dataSource={savePreview.skipped}
              renderItem={(h) => (
                <List.Item>
                  <List.Item.Meta
                    title={h.instrument.name}
                    description={`${h.instrument.isin_prefix} · ${h.instrument.category}`}
                  />
                </List.Item>
              )}
            />
          )}
        </Modal>
      </div>
    </LandingLayout>
  );
}

export default FixedIncomePortfolioBuilderPage;
